import { PlusOutlined } from '@ant-design/icons'
import { Alert, Button, DatePicker } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { observer } from 'mobx-react-lite'
import { IoSaveOutline } from 'react-icons/io5'
import { useMemo } from 'react'

import { MultiAssignmentFieldCell } from '@/Containers/AssignmentSection/AssignmentContainer/Components/MultiAssignmentFieldCell'
import {
    clusterCodeOf,
    regionFilterFor,
} from '@/Containers/AssignmentSection/AssignmentContainer/Helpers/clusterRegion'
import { FormRow, FormSection } from '@/Ship/Components/Form/FormSection'
import { AppDrawer, DEFAULT_DETAIL_WIDTH } from '@/Ship/Components/UI/AppDrawer/AppDrawer'
import { DrawerDirtyGuard } from '@/Ship/Components/UI/AppDrawer/DrawerDirtyGuard'
import { cx } from '@/Ship/Helpers/cssClass'
import { pluralizeUk } from '@/Ship/Helpers/pluralize'
import { useNotification } from '@/Ship/Hooks/useNotification'

import { ShiftResource } from '../Resources/ShiftResource'
import { MAX_PERIOD_DAYS, MAX_ROWS, isWithinPeriod } from '../Stores/MultiShiftDrawerStore'
import { MultiShiftRow } from './MultiShiftRow'

import type { MultiShiftDrawerStore } from '../Stores/MultiShiftDrawerStore'

import styles from './MultiShiftDrawer.module.css'

interface Props {
    store: MultiShiftDrawerStore
    onCreated?: () => void
}

const DATE = 'YYYY-MM-DD'

/** Поле ресурсу змін, щоб кластер і регіон були тими самими select-ами, що й у формі зміни */
const fieldDef = (name: string): Record<string, any> => (ShiftResource.fields as any)?.[name] ?? { type: 'string' }

/** Дата з підписом у розмітці полів форми (BaseFieldTemplate) */
const DateField = ({
    id,
    label,
    value,
    onChange,
    disabledDate,
}: {
    id: string
    label: string
    value: string | null
    onChange: (value: string | null) => void
    disabledDate?: (date: Dayjs) => boolean
}) => (
    <div className="form-field form-field-default">
        <label className="field-label" htmlFor={id}>
            <span className="field-required">*</span>
            {label}
        </label>
        <DatePicker
            id={id}
            className={cx(styles, 'date')}
            format="DD.MM.YYYY"
            value={value ? dayjs(value, DATE) : null}
            onChange={(date) => onChange(date ? date.format(DATE) : null)}
            {...(disabledDate ? { disabledDate } : {})}
        />
    </div>
)

/**
 * Вікно «Додати кілька» на /shifts: період (до 5 днів), кластер/регіон і таблиця
 * «водій + ТЗ». Кожен рядок отримує зміну на кожну дату періоду; якщо хоч одна з них уже
 * існує, не створюється жодна, а конфлікти показуються списком над таблицею.
 *
 * Оформлення — як у «Створити кілька завдань»: AppDrawer стандартної ширини, кнопка дії
 * в шапці, секції FormSection; закриття з уже введеним питає підтвердження.
 */
export const MultiShiftDrawer = observer(({ store, onCreated }: Props) => {
    const { showSuccess, showError } = useNotification()
    const notify = { success: showSuccess, error: showError }

    const clusterCode = clusterCodeOf(store.cluster)
    const regionDef = useMemo(
        () => ({
            ...fieldDef('region'),
            inputProps: {
                ...fieldDef('region')['inputProps'],
                filter: regionFilterFor(clusterCode),
                disabled: !clusterCode,
            },
        }),
        [clusterCode]
    )

    const days = store.days
    const count = store.shiftsCount

    return (
        <AppDrawer
            title="Створити кілька змін"
            placement="right"
            size={DEFAULT_DETAIL_WIDTH}
            open={store.open}
            onClose={() => store.closeDrawer()}
            destroyOnHidden
            extra={
                <Button
                    type="primary"
                    icon={<IoSaveOutline size={19} />}
                    loading={store.submitting}
                    onClick={() => store.submit(notify, onCreated)}
                >
                    Зберегти
                </Button>
            }
        >
            {store.open && <DrawerDirtyGuard isDirty={() => store.isDirty} />}
            <div className={cx(styles, 'form')}>
                {store.errors.length > 0 && (
                    <Alert
                        className={cx(styles, 'errorsAlert')}
                        type="error"
                        showIcon
                        message="Зміни не створено"
                        description={
                            <ul className={cx(styles, 'errors')}>
                                {store.errors.map((error, i) => (
                                    <li key={i}>{error}</li>
                                ))}
                            </ul>
                        }
                    />
                )}

                <FormSection title="Період" hint={`До ${MAX_PERIOD_DAYS} днів: зміна створюється на кожну дату`}>
                    <FormRow>
                        <DateField
                            id="multi-shift-date-from"
                            label="Дата від"
                            value={store.dateFrom}
                            onChange={(value) => store.setDateFrom(value)}
                        />
                        <DateField
                            id="multi-shift-date-to"
                            label="Дата до"
                            value={store.dateTo}
                            onChange={(value) => store.setDateTo(value)}
                            disabledDate={(date) =>
                                !!store.dateFrom && !isWithinPeriod(store.dateFrom, date.format(DATE))
                            }
                        />
                    </FormRow>
                </FormSection>

                <FormSection
                    title="Географія"
                    hint={clusterCode ? undefined : 'Спершу оберіть кластер — регіони фільтруються за ним'}
                >
                    <FormRow>
                        <MultiAssignmentFieldCell
                            fieldName="cluster"
                            label={fieldDef('cluster')['label']}
                            fieldDef={fieldDef('cluster')}
                            onChange={(value) => store.setCluster(value)}
                        />
                        <MultiAssignmentFieldCell
                            fieldName="region"
                            label={fieldDef('region')['label']}
                            fieldDef={regionDef}
                            initialValue={store.region}
                            onChange={(value) => store.setRegion(value)}
                            disabled={!clusterCode}
                        />
                    </FormRow>
                </FormSection>

                <FormSection
                    title="Водії й транспорт"
                    hint={
                        count > 0
                            ? `Буде створено ${count} ${pluralizeUk(count, ['зміну', 'зміни', 'змін'])}: ` +
                              `${count / days} ${pluralizeUk(count / days, ['рядок', 'рядки', 'рядків'])} × ` +
                              `${days} ${pluralizeUk(days, ['день', 'дні', 'днів'])}`
                            : 'Кожен рядок — водій і його ТЗ; порожні рядки не зберігаються'
                    }
                >
                    <div className={cx(styles, 'rows')} role="list" aria-label="Водії й транспорт">
                        <div className={cx(styles, 'rowsHead')} aria-hidden="true">
                            <span>№</span>
                            <span>
                                Водій <span className={cx(styles, 'required')}>*</span>
                            </span>
                            <span>
                                Транспортний засіб <span className={cx(styles, 'required')}>*</span>
                            </span>
                            <span />
                        </div>
                        {store.rows.map((row, index) => (
                            <MultiShiftRow key={row.id} store={store} row={row} index={index} />
                        ))}
                    </div>
                    <Button
                        type="dashed"
                        block
                        icon={<PlusOutlined />}
                        disabled={store.rows.length >= MAX_ROWS}
                        onClick={() => store.addRow()}
                    >
                        Додати водія
                    </Button>
                </FormSection>
            </div>
        </AppDrawer>
    )
})
